/**
 * ChainVault WP - Wallet Page
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import AssetList from '../components/AssetList';
import toast from 'react-hot-toast';
import { playSendSound, playErrorSound, resumeAudio } from '../utils/sounds';
import { sendAsset } from '../utils/sendAsset';

function WalletPage() {

    const navigate = useNavigate();
    const location = useLocation();
    const [ sendAssetOverride, setSendAssetOverride ] = useState( null );

    const {
        address,
        testnetMode,
        balances,
        selectedNetwork,
        currentNetwork,
        isLoadingBalance,
        walletType,
        builtInWallet,
        fetchEthBalance,
        fetchSolanaBalance,
        getUSDValue,
        prices,
        solTokens,
        sendFromBuiltIn,
        setShowOnboarding,
        switchNetwork,
    } = useWallet();

    const [ showUSD,          setShowUSD         ] = useState( false );
    const [ totalPortfolioUSD, setTotalPortfolioUSD ] = useState( null );
    const [ showDepositModal, setShowDepositModal ] = useState( false );
    const [ showSendModal,    setSendModal        ] = useState( false );
    const [ sendTo,           setSendTo           ] = useState( '' );
    const [ sendAmount,       setSendAmount       ] = useState( '' );
    const [ isSending,        setIsSending        ] = useState( false );
    const [ networkFilter,    setNetworkFilter    ] = useState( 'all' );
    const [ showAddToken,     setShowAddToken     ] = useState( false );
    const [ tokenAddress,     setTokenAddress     ] = useState( '' );
    const [ tokenType,        setTokenType        ] = useState( 'erc20' ); /* erc20 or spl */
    const [ tokenInfo,        setTokenInfo        ] = useState( null );
    const [ isLoadingToken,   setIsLoadingToken   ] = useState( false );
    const [ customTokens,     setCustomTokens     ] = useState( () => {
        try { return JSON.parse( localStorage.getItem( 'chainvault_custom_tokens' ) || '[]' ); } catch(e) { return []; }
    });

    /* Refresh custom tokens when balance updates */
    useEffect( () => {
        const refresh = () => {
            try {
                const updated = JSON.parse( localStorage.getItem( 'chainvault_custom_tokens' ) || '[]' );
                setCustomTokens( updated );
            } catch(e) {}
        };
        window.addEventListener( 'chainvault_tokens_updated', refresh );
        return () => window.removeEventListener( 'chainvault_tokens_updated', refresh );
    }, [] );

    const currentBalance = balances[ selectedNetwork ] || '0.000000';
    const usdValue       = getUSDValue( currentBalance, selectedNetwork );

    /* Convert the total portfolio USD value into native-coin-equivalent units,
       so toggling to ETH/SOL/etc shows the WHOLE portfolio's worth in that coin,
       not just the raw native balance. */
    const coinMapDisplay  = { ethereum: 'ethereum', bsc: 'binancecoin', polygon: 'matic-network', base: 'ethereum', solana: 'solana', bitcoin: 'bitcoin' };
    const nativePriceUSD  = prices?.[ coinMapDisplay[ selectedNetwork ] ]?.usd || 0;
    const totalUSDNumeric = parseFloat( ( ( totalPortfolioUSD || usdValue || '$0' ) + '' ).replace( '$', '' ) ) || 0;
    const nativeEquivalent = nativePriceUSD > 0 ? ( totalUSDNumeric / nativePriceUSD ).toFixed( 6 ) : currentBalance;
    const formattedUSDTotal = '$' + totalUSDNumeric.toFixed( 2 );

    /* Calculate total portfolio USD value including ERC20 tokens */
    useEffect( () => {
        const calcTotal = async () => {
            try {
                const ethAddr = walletType === 'builtin' && builtInWallet ? builtInWallet.ethereum.address : address;
                if ( ! ethAddr || ! prices ) return;

                /* Only sum assets on the CURRENT selected network */
                let total = 0;
                const coinMap = { ethereum: 'ethereum', bsc: 'binancecoin', polygon: 'matic-network', base: 'ethereum', solana: 'solana', bitcoin: 'bitcoin' };
                const currentNet = selectedNetwork;
                const coinId = coinMap[ currentNet ];
                const nativePrice = prices?.[ coinId ]?.usd;
                if ( nativePrice ) {
                    const amount = parseFloat( balances[ currentNet ] || 0 );
                    if ( ! isNaN( amount ) ) total += amount * nativePrice;
                }

                /* Add ERC20 token values */
                const { ethers } = await import( 'ethers' );
                const provider = new ethers.JsonRpcProvider( 'https://ethereum-rpc.publicnode.com' );
                const abi = [ 'function balanceOf(address) view returns (uint256)' ];
                /* Token list per network — only fetch tokens for the current chain */
                const allErc20Tokens = {
                    ethereum: [
                        { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6,  price: 1.00 },
                        { address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6,  price: 1.00 },
                        { address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', decimals: 8,  price: prices?.['bitcoin']?.usd || 0 },
                        { address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', decimals: 18, price: prices?.['uniswap']?.usd || 0 },
                    ],
                    bsc: [
                        { address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18, price: 1.00 },
                        { address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', decimals: 18, price: 1.00 },
                    ],
                    polygon: [
                        { address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6,  price: 1.00 },
                        { address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', decimals: 6,  price: 1.00 },
                    ],
                    base: [
                        { address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6,  price: 1.00 },
                    ],
                    solana: [], /* SOL SPL tokens handled separately via solTokens from WalletContext */
                };

                const rpcMap = {
                    ethereum: 'https://ethereum-rpc.publicnode.com',
                    bsc:      'https://bsc-dataseed.binance.org',
                    polygon:  'https://polygon-bor-rpc.publicnode.com',
                    base:     'https://mainnet.base.org',
                };

                const erc20List = allErc20Tokens[ currentNet ] || [];
                if ( erc20List.length > 0 && rpcMap[ currentNet ] ) {
                    const netProvider = new ethers.JsonRpcProvider( rpcMap[ currentNet ] );
                    await Promise.all( erc20List.map( async ( tok ) => {
                        try {
                            const contract = new ethers.Contract( tok.address, abi, netProvider );
                            const bal = await contract.balanceOf( ethAddr );
                            const amount = parseFloat( ethers.formatUnits( bal, tok.decimals ) );
                            if ( ! isNaN( amount ) && amount > 0 ) total += amount * tok.price;
                        } catch(e) {}
                    }) );
                }

                /* Custom / user-added tokens on the current network (e.g. custom SPL/ERC20 tokens
                   the user created or added — NOT the same as real live wallet holdings below). */
                const knownStableSymbols = [ 'USDT', 'USDC', 'BUSD', 'DAI' ];
                const currentNetworkTokens = ( customTokens || [] ).filter( t => t.network === currentNet );
                currentNetworkTokens.forEach( tok => {
                    const amount = parseFloat( tok.balance || 0 );
                    if ( isNaN( amount ) || amount <= 0 ) return;

                    const symbolUpper = ( tok.symbol || '' ).toUpperCase();
                    if ( knownStableSymbols.includes( symbolUpper ) ) {
                        total += amount * 1.00;
                    } else if ( tok.price && ! isNaN( parseFloat( tok.price ) ) ) {
                        total += amount * parseFloat( tok.price );
                    }
                    /* Unknown/custom tokens without a real price (e.g. test tokens like NTA, PRD)
                       contribute $0 to the total — they still show in Your Assets with their raw balance. */
                });

                /* Real, live-fetched Solana SPL token holdings (from WalletContext's actual on-chain fetch).
                   This is the genuine source for wallet USDT/USDC balances on Solana — price only
                   recognized stablecoins, everything else contributes $0 until priced properly. */
                if ( currentNet === 'solana' && solTokens && solTokens.length > 0 ) {
                    solTokens.forEach( tok => {
                        const amount = parseFloat( tok.balance || 0 );
                        if ( isNaN( amount ) || amount <= 0 ) return;
                        const symbolUpper = ( tok.symbol || '' ).toUpperCase();
                        if ( knownStableSymbols.includes( symbolUpper ) ) {
                            total += amount * 1.00;
                        }
                    });
                }

                setTotalPortfolioUSD( '$' + total.toFixed( 2 ) );
            } catch(e) {}
        };
        calcTotal();
    }, [ balances, prices, walletType, builtInWallet, address, customTokens, selectedNetwork ] ); // eslint-disable-line

    const getDepositAddress = () => {
        if ( walletType === 'builtin' && builtInWallet ) {
            if ( selectedNetwork === 'solana' )  return builtInWallet.solana.address;
            if ( selectedNetwork === 'bitcoin' ) return builtInWallet.bitcoin.address;
            return builtInWallet.ethereum.address;
        }
        return address;
    };

    const depositAddress = getDepositAddress();

    useEffect( () => {
        if ( ! address ) return;

        if ( selectedNetwork === 'solana' ) {
            const solAddress = walletType === 'builtin' && builtInWallet
                ? builtInWallet.solana.address
                : address;
            fetchSolanaBalance( solAddress, true );
        } else {
            const ethAddress = walletType === 'builtin' && builtInWallet
                ? builtInWallet.ethereum.address
                : address;
            fetchEthBalance( ethAddress, selectedNetwork, true );
        }
    }, [ address, selectedNetwork ] ); // eslint-disable-line

    const handleNetworkFilter = ( value ) => {
        setNetworkFilter( value );
        if ( value === 'all' ) return;
        switchNetwork( value );
    };

    /* Sync network filter when selectedNetwork changes from navbar */
    useEffect( () => {
        if ( selectedNetwork && selectedNetwork !== networkFilter ) {
            setNetworkFilter( selectedNetwork );
        }
    }, [ selectedNetwork ] ); // eslint-disable-line

    const handleLookupToken = async () => {
        if ( !tokenAddress ) return;
        setIsLoadingToken( true );
        setTokenInfo( null );
        try {
            const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';

            if ( tokenType === 'spl' ) {
                /* SPL Token lookup */
                const { Buffer } = await import( 'buffer' );
                window.Buffer = window.Buffer || Buffer;
                const web3 = await import( '@solana/web3.js' );
                const splToken = await import( '@solana/spl-token' );
                const rpc = isTestnet ? 'https://api.devnet.solana.com' : 'https://api.mainnet-beta.solana.com';
                const connection = new web3.Connection( rpc, 'confirmed' );
                const mintPubkey = new web3.PublicKey( tokenAddress );
                const mintInfo = await splToken.getMint( connection, mintPubkey );
                const activeAddr = walletType === 'builtin' && builtInWallet ? builtInWallet.solana.address : address;
                let balance = '0';
                try {
                    const solPubkey = new web3.PublicKey( activeAddr );
                    const tokenAccount = await splToken.getAssociatedTokenAddress( mintPubkey, solPubkey );
                    const accountInfo = await splToken.getAccount( connection, tokenAccount );
                    balance = ( Number( accountInfo.amount ) / Math.pow( 10, mintInfo.decimals ) ).toString();
                } catch(e) { balance = '0'; }
                /* Fetch token name/symbol/image from WordPress metadata if available */
                let tokenName = 'SPL Token';
                let tokenSymbol = tokenAddress.slice(0,6).toUpperCase();
                let tokenIcon = '';
                try {
                    const wpBase = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';
                    const metaRes = await fetch( wpBase + '/metadata/' + tokenAddress );
                    if ( metaRes.ok ) {
                        const metaData = await metaRes.json();
                        if ( metaData.name )   tokenName   = metaData.name;
                        if ( metaData.symbol ) tokenSymbol = metaData.symbol;
                        if ( metaData.image )  tokenIcon   = metaData.image;
                    }
                } catch(e) { /* metadata fetch failed, use defaults */ }
                setTokenInfo({ name: tokenName, symbol: tokenSymbol, decimals: mintInfo.decimals, balance, address: tokenAddress, network: 'solana', type: 'spl', needsMeta: true, icon: tokenIcon });
            } else {
                /* ERC20 Token lookup */
                const rpc = isTestnet ? 'https://ethereum-sepolia-rpc.publicnode.com' : 'https://ethereum-rpc.publicnode.com';
                const { ethers } = await import( 'ethers' );
                const provider = new ethers.JsonRpcProvider( rpc );
                const abi = [
                    'function name() view returns (string)',
                    'function symbol() view returns (string)',
                    'function decimals() view returns (uint8)',
                    'function balanceOf(address) view returns (uint256)',
                ];
                const contract = new ethers.Contract( tokenAddress, abi, provider );
                const activeAddr = walletType === 'builtin' && builtInWallet ? builtInWallet.ethereum.address : address;
                const [ name, symbol, decimals, balance ] = await Promise.all([
                    contract.name(),
                    contract.symbol(),
                    contract.decimals(),
                    contract.balanceOf( activeAddr ),
                ]);
                const formatted = ethers.formatUnits( balance, decimals );
                setTokenInfo({ name, symbol, decimals: Number(decimals), balance: formatted, address: tokenAddress, network: 'ethereum', type: 'erc20' });
            }
        } catch(err) {
            toast.error( 'Token not found. Check the address.' );
        } finally {
            setIsLoadingToken( false );
        }
    };

    const handleAddToken = () => {
        if ( !tokenInfo ) return;
        const existing = customTokens.find( t => t.address.toLowerCase() === tokenInfo.address.toLowerCase() );
        if ( existing ) { toast.error( 'Token already added.' ); return; }
        const updated = [ ...customTokens, tokenInfo ];
        setCustomTokens( updated );
        localStorage.setItem( 'chainvault_custom_tokens', JSON.stringify( updated ) );
        window.dispatchEvent( new Event( 'chainvault_tokens_updated' ) );
        toast.success( tokenInfo.symbol + ' added to your wallet!' );
        setShowAddToken( false );
        setTokenAddress( '' );
        setTokenInfo( null );
    };

    const copyAddress = () => {
        if ( depositAddress ) {
            navigator.clipboard.writeText( depositAddress );
            toast.success( 'Address copied!' );
        }
    };

    /**
     * Handle send transaction
     * Plays send sound and voice on success for BOTH ETH and SOL
     * Plays error sound on failure
     */
    const handleSend = async () => {
        setIsSending( true );
        try {
            /* When opened normally (main card's Send button), default to the current
               network's native coin — same behavior as before. When opened from a token's
               detail page, sendAssetOverride carries the specific asset to send instead. */
            const targetAsset = sendAssetOverride || {
                symbol:  currentNetwork.symbol,
                network: selectedNetwork,
                id:      selectedNetwork,
            };
            const result = await sendAsset( { asset: targetAsset, sendTo, sendAmount, walletType, builtInWallet, sendFromBuiltIn } );
            await resumeAudio();
            playSendSound();
            toast.success( result.hash ? `Sent! ${ result.hash.slice( 0, 16 ) }...` : 'Transaction submitted!' );
            setSendModal( false );
            setSendAssetOverride( null );
            setSendTo( '' );
            setSendAmount( '' );
        } catch ( err ) {
            console.error( err );
            playErrorSound();
            toast.error( err.message || 'Transaction failed.' );
        } finally {
            setIsSending( false );
        }
    };

    /* If navigated here from a token's detail page wanting to Send or Receive
       that specific asset, open the right modal automatically. */
    useEffect( () => {
        const navState = location.state;
        if ( ! navState ) return;

        if ( navState.openSendForAsset ) {
            const target = navState.openSendForAsset;
            if ( target.network && target.network !== selectedNetwork ) {
                switchNetwork( target.network );
            }
            setSendAssetOverride( target );
            setSendModal( true );
        }

        if ( navState.openDepositForNetwork ) {
            const net = navState.openDepositForNetwork;
            if ( net && net !== selectedNetwork ) {
                switchNetwork( net );
            }
            setShowDepositModal( true );
        }

        /* Clear the navigation state so a page refresh doesn't reopen the modal */
        navigate( location.pathname, { replace: true, state: {} } );
    }, [] ); // eslint-disable-line

    /* Use touch detection + screen width for reliable mobile detection.
     * window.innerWidth alone can fail on some mobile browsers that
     * report desktop widths when the page is not fully loaded yet. */
    const isMobile = window.innerWidth <= 900 || ( typeof window.ontouchstart !== 'undefined' && window.innerWidth <= 1024 );

    /* ── Mobile Layout ── */
    if ( isMobile ) {
        return (
            <div style={{ padding: '12px 12px 20px' }}>

                { /* Balance Card */ }
                <div style={{
                    background: 'linear-gradient(135deg, #0f0f22, #1a1040)',
                    borderRadius: '20px', padding: '20px',
                    border: '1px solid rgba(124,111,255,0.2)',
                    marginBottom: '16px'
                }}>
                    <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                        Total Balance
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: '#fff', letterSpacing: '-2px', marginBottom: '4px' }}>
                        { isLoadingBalance ? '...' : ( showUSD ? formattedUSDTotal : nativeEquivalent + ' ' + ( selectedNetwork === 'base' ? 'ETH (Base)' : currentNetwork.symbol ) ) }
                    </div>
                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '16px' }}>
                        { showUSD
                            ? nativeEquivalent + ' ' + ( selectedNetwork === 'base' ? 'ETH (Base)' : currentNetwork.symbol ) + ' • Your total balance'
                            : formattedUSDTotal + ' • Your total balance' }
                    </div>

                    { /* 4 Action Buttons — Phantom style */ }
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginTop: '4px' }}>
                        { [
                            { icon: '↑', label: 'Send',    action: () => setSendModal(true) },
                            { icon: '⇄', label: 'Swap',    action: () => navigate('/exchange') },
                            { icon: '↓', label: 'Receive', action: () => setShowDepositModal(true) },
                            { icon: '＄', label: 'Buy',     action: () => navigate('/buy') },
                        ].map( btn => (
                            <button key={btn.label} onClick={btn.action} style={{
                                background: 'rgba(255,255,255,0.07)',
                                border: 'none',
                                borderRadius: '16px', padding: '14px 4px',
                                color: '#fff', fontSize: '11px', fontWeight: '600',
                                cursor: 'pointer', fontFamily: 'inherit',
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', gap: '6px'
                            }}>
                                <span style={{
                                    width: '36px', height: '36px', borderRadius: '50%',
                                    background: 'rgba(124,111,255,0.25)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '18px', color: '#a78bfa'
                                }}>{ btn.icon }</span>
                                { btn.label }
                            </button>
                        ))}
                    </div>
                </div>

                { /* Assets Section */ }
                <div style={{ marginBottom: '12px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>Tokens</span>
                    <select value={networkFilter} onChange={e=>setNetworkFilter(e.target.value)}
                        style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: '8px', color: '#aaa', fontSize: '12px', padding: '6px 10px' }}>
                        <option value="all">All networks</option>
                        <option value="ethereum">Ethereum</option>
                        <option value="solana">Solana</option>
                        <option value="bsc">BNB Chain</option>
                        <option value="polygon">Polygon</option>
                        <option value="base">Base</option>
                    </select>
                </div>
                <AssetList networkFilter={networkFilter} tokens={customTokens} />

                { /* Send Modal */ }
                { showSendModal && (
                    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:400,display:'flex',alignItems:'flex-end' }}>
                        <div style={{ background:'#0f0f22',borderRadius:'20px 20px 0 0',padding:'24px',width:'100%',border:'1px solid rgba(124,111,255,0.2)' }}>
                            <div style={{ fontSize:'16px',fontWeight:'700',color:'#fff',marginBottom:'16px' }}>Send { sendAssetOverride?.symbol || currentNetwork.symbol }</div>
                            <input placeholder="Recipient address" value={sendTo} onChange={e=>setSendTo(e.target.value)}
                                style={{ width:'100%',background:'#1a1a35',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',padding:'12px',color:'#fff',fontSize:'14px',marginBottom:'12px',boxSizing:'border-box' }} />
                            <input placeholder="Amount" type="number" value={sendAmount} onChange={e=>setSendAmount(e.target.value)}
                                style={{ width:'100%',background:'#1a1a35',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',padding:'12px',color:'#fff',fontSize:'14px',marginBottom:'16px',boxSizing:'border-box' }} />
                            <div style={{ display:'flex',gap:'10px' }}>
                                <button onClick={()=>{setSendModal(false);setSendAssetOverride(null);}} style={{ flex:1,padding:'12px',background:'#1a1a35',border:'none',borderRadius:'10px',color:'#aaa',fontSize:'14px',cursor:'pointer',fontFamily:'inherit' }}>Cancel</button>
                                <button onClick={handleSend} disabled={isSending} style={{ flex:1,padding:'12px',background:'linear-gradient(135deg,#7c6fff,#FC72FF)',border:'none',borderRadius:'10px',color:'#fff',fontSize:'14px',fontWeight:'700',cursor:'pointer',fontFamily:'inherit' }}>
                                    { isSending ? 'Sending...' : 'Send' }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                { /* Deposit Modal */ }
                { showDepositModal && (
                    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:400,display:'flex',alignItems:'flex-end' }}>
                        <div style={{ background:'#0f0f22',borderRadius:'20px 20px 0 0',padding:'24px',width:'100%',border:'1px solid rgba(124,111,255,0.2)' }}>
                            <div style={{ fontSize:'16px',fontWeight:'700',color:'#fff',marginBottom:'8px' }}>Receive { currentNetwork.symbol }</div>
                            <div style={{ fontSize:'12px',color:'#888',marginBottom:'16px' }}>Send only { currentNetwork.symbol } to this address</div>
                            <div style={{ background:'#1a1a35',borderRadius:'10px',padding:'14px',wordBreak:'break-all',fontSize:'13px',color:'#a78bfa',marginBottom:'16px',textAlign:'center' }}>
                                { getDepositAddress() }
                            </div>
                            <button onClick={()=>{ navigator.clipboard.writeText(getDepositAddress()); toast.success('Address copied!'); }}
                                style={{ width:'100%',padding:'12px',background:'linear-gradient(135deg,#7c6fff,#FC72FF)',border:'none',borderRadius:'10px',color:'#fff',fontSize:'14px',fontWeight:'700',cursor:'pointer',fontFamily:'inherit',marginBottom:'10px' }}>
                                Copy Address
                            </button>
                            <button onClick={()=>setShowDepositModal(false)}
                                style={{ width:'100%',padding:'12px',background:'#1a1a35',border:'none',borderRadius:'10px',color:'#aaa',fontSize:'14px',cursor:'pointer',fontFamily:'inherit' }}>
                                Close
                            </button>
                        </div>
                    </div>
                )}


            </div>
        );
    }

    return (
        <div className="cv-page">
            { testnetMode && (
                <div style={{ background:'linear-gradient(135deg,rgba(255,170,51,0.15),rgba(255,100,0,0.1))', border:'1px solid rgba(255,170,51,0.4)', borderRadius:'12px', padding:'10px 16px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'10px', fontSize:'13px', color:'#ffaa33', fontWeight:'600' }}>
                    <span>🧪</span>
                    <span>Testnet Mode Active — Balances and transactions use test networks. No real funds involved.</span>
                    <span style={{marginLeft:'auto',fontSize:'11px',opacity:0.7}}>Settings → Network to disable</span>
                </div>
            ) }
            <div className="cv-wallet-layout">

                { /* Left Panel */ }
                <div className="cv-wallet-left">

                    <div className="cv-balance-card">
                        <div className="cv-balance-toggle">
                            <button
                                className={ `cv-toggle-btn ${ ! showUSD ? 'active' : '' }` }
                                onClick={ () => setShowUSD( false ) }
                            >
                                { currentNetwork.symbol }
                            </button>
                            <button
                                className={ `cv-toggle-btn ${ showUSD ? 'active' : '' }` }
                                onClick={ () => setShowUSD( true ) }
                            >
                                USD
                            </button>
                            <span style={{ marginLeft: 'auto', color: '#888', fontSize: '13px' }}>
                                Your total balance
                            </span>
                        </div>

                        <div className="cv-balance-amount">
                            { isLoadingBalance ? (
                                <span className="cv-loading">Loading...</span>
                            ) : (
                                <>
                                    <span className="cv-balance-symbol">
                                        { showUSD ? '$' :
                                          selectedNetwork === 'base' ? '🔵' :
                                          currentNetwork.symbol === 'ETH' ? (
                                              <svg width="30" height="30" viewBox="0 0 24 24" style={{ verticalAlign: '-6px' }}>
                                                  <path fill="currentColor" d="M12 1.5L5.5 12.25 12 16l6.5-3.75L12 1.5z" />
                                                  <path fill="currentColor" opacity="0.7" d="M12 17.3L5.5 13.5 12 22.5l6.5-9L12 17.3z" />
                                              </svg>
                                          ) :
                                          currentNetwork.symbol === 'BTC' ? '₿' :
                                          currentNetwork.symbol === 'SOL' ? '◎' :
                                          currentNetwork.symbol }
                                    </span>
                                    <span className="cv-balance-number">
                                        { showUSD ? totalUSDNumeric.toFixed(2) : nativeEquivalent }
                                    </span>
                                </>
                            ) }
                        </div>

                        { address && (
                            <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
                                { showUSD
                                    ? `${ nativeEquivalent } ${ selectedNetwork === 'base' ? 'ETH (Base)' : currentNetwork.symbol } • Your total balance`
                                    : `${ formattedUSDTotal } • Your total balance` }
                            </div>
                        ) }

                        <div className="cv-balance-actions">
                            <button
                                className="cv-action-btn primary"
                                onClick={ () => setShowDepositModal( true ) }
                            >
                                Deposit
                            </button>
                            <button
                                className="cv-action-btn secondary"
                                onClick={ () => setSendModal( true ) }
                                disabled={ ! address }
                            >
                                Send
                            </button>
                        </div>
                    </div>

                    <div className="cv-faq-card">
                        <h3>FAQ</h3>
                        <details className="cv-faq-item">
                            <summary>How are my private keys stored?</summary>
                            <p>Your private keys are stored securely in your browser's local storage and never sent to any server.</p>
                        </details>
                        <details className="cv-faq-item">
                            <summary>What are the fees involved?</summary>
                            <p>A small service fee is charged on each transaction. Network gas fees also apply.</p>
                        </details>
                        <details className="cv-faq-item">
                            <summary>Why is the mining fee high?</summary>
                            <p>Mining fees depend on network congestion. Try again when the network is less busy.</p>
                        </details>
                    </div>

                </div>

                { /* Right Panel */ }
                <div className="cv-wallet-right">

                    <div className="cv-promo-cards">
                        <div
                            className="cv-promo-card purple"
                            style={{ cursor: 'pointer' }}
                            onClick={ copyAddress }
                        >
                            <div className="cv-promo-icon">🔒</div>
                            <p>Please backup your wallet</p>
                        </div>
                        <div className="cv-promo-card blue">
                            <div className="cv-promo-icon">⚡</div>
                            <p>Try BTC/USDT AtomicSwap exchange</p>
                        </div>
                        <div className="cv-promo-card teal">
                            <div className="cv-promo-icon">💳</div>
                            <p>Deposit using VISA/MC</p>
                        </div>
                    </div>

                    { ! address ? (

                        <div className="cv-connect-prompt">
                            <h2>Your Assets</h2>
                            <p>Here you can safely store, send and receive assets</p>
                            <button
                                className="cv-btn-primary full-width"
                                onClick={ () => setShowOnboarding( true ) }
                            >
                                Create or Connect Wallet
                            </button>
                        </div>

                    ) : (
                        <>
                        { (window.chainvaultSettings && window.chainvaultSettings.adSlot1) && <div className="cv-ad-slot" style={{margin:'16px 0',textAlign:'center'}} dangerouslySetInnerHTML={{__html: window.chainvaultSettings.adSlot1}} /> }
                        <div className="cv-assets-section">
                            <h2>Your Assets</h2>
                            <p>Here you can safely store, send and receive assets</p>

                            <div className="cv-network-filter">
                                <select
                                    className="cv-select"
                                    value={ networkFilter }
                                    onChange={ e => handleNetworkFilter( e.target.value ) }
                                >
                                    <option value="all">All networks</option>
                                    <option value="ethereum">Ethereum</option>
                                    <option value="bsc">BNB Smart Chain</option>
                                    <option value="polygon">Polygon</option>
                                    <option value="base">Base</option>
                                    <option value="solana">Solana</option>
                                </select>
                            </div>

                            <AssetList networkFilter={ networkFilter } tokens={ customTokens } />

                            <button className="cv-add-token-btn" onClick={()=>setShowAddToken(true)}>+ Add custom token</button>
                            <button className="cv-add-token-btn">+ Add currency</button>
                        </div>
                        </>

                    ) }

                </div>

            </div>

            { /* Deposit Modal */ }
            { showDepositModal && (
                <div
                    className="cv-modal-overlay"
                    onClick={ () => setShowDepositModal( false ) }
                >
                    <div
                        className="cv-modal"
                        onClick={ e => e.stopPropagation() }
                    >
                        <div className="cv-modal-header">
                            <h3>Deposit { currentNetwork.symbol }</h3>
                            <button
                                className="cv-modal-close"
                                onClick={ () => setShowDepositModal( false ) }
                            >✕</button>
                        </div>

                        { depositAddress ? (
                            <div className="cv-deposit-content">
                                <p style={{ color: '#888', fontSize: '14px', marginBottom: '16px' }}>
                                    Send { currentNetwork.symbol } to this address:
                                </p>
                                <div className="cv-address-display">
                                    <code>{ depositAddress }</code>
                                    <button className="cv-copy-btn" onClick={ copyAddress }>
                                        Copy
                                    </button>
                                </div>
                                <p style={{ color: '#888', fontSize: '12px', marginTop: '12px' }}>
                                    Only send { currentNetwork.symbol } to this address.
                                    Sending other tokens may result in permanent loss.
                                </p>
                            </div>
                        ) : (
                            <div className="cv-deposit-content">
                                <p style={{ color: '#888' }}>Please connect your wallet first.</p>
                            </div>
                        ) }
                    </div>
                </div>
            ) }

            { /* Send Modal */ }
            { showSendModal && (
                <div
                    className="cv-modal-overlay"
                    onClick={ () => setSendModal( false ) }
                >
                    <div
                        className="cv-modal"
                        onClick={ e => e.stopPropagation() }
                    >
                        <div className="cv-modal-header">
                            <h3>Send { currentNetwork.symbol }</h3>
                            <button
                                className="cv-modal-close"
                                onClick={ () => setSendModal( false ) }
                            >✕</button>
                        </div>

                        <div className="cv-send-content">
                            <div className="cv-field-group">
                                <label>Recipient Address</label>
                                <input
                                    type="text"
                                    className="cv-input"
                                    placeholder={ selectedNetwork === 'solana' ? 'Solana address...' : '0x...' }
                                    value={ sendTo }
                                    onChange={ e => setSendTo( e.target.value ) }
                                />
                            </div>

                            <div className="cv-field-group">
                                <label>Amount ({ currentNetwork.symbol })</label>
                                <input
                                    type="number"
                                    className="cv-input"
                                    placeholder="0.00"
                                    value={ sendAmount }
                                    onChange={ e => setSendAmount( e.target.value ) }
                                />
                                <span style={{ fontSize: '12px', color: '#888' }}>
                                    Balance: { currentBalance } { currentNetwork.symbol }
                                    { usdValue !== '$0.00' && ` ≈ ${ usdValue }` }
                                </span>
                            </div>

                            <button
                                className="cv-btn-primary full-width"
                                onClick={ handleSend }
                                disabled={ isSending }
                            >
                                { isSending ? 'Sending...' : `Send ${ sendAssetOverride?.symbol || currentNetwork.symbol }` }
                            </button>
                        </div>
                    </div>
                </div>
            ) }

        { /* Add Custom Token Modal */ }
        { showAddToken && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(6px)'}} onClick={()=>setShowAddToken(false)}>
                <div style={{background:'#111',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'20px',padding:'28px',width:'100%',maxWidth:'420px',margin:'16px'}} onClick={e=>e.stopPropagation()}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
                        <h3 style={{color:'#fff',margin:0,fontSize:'18px',fontWeight:'800'}}>Add Custom Token</h3>
                        <button onClick={()=>setShowAddToken(false)} style={{background:'none',border:'none',color:'#888',fontSize:'20px',cursor:'pointer'}}>✕</button>
                    </div>
                    <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
                        <button onClick={()=>{setTokenType('erc20');setTokenInfo(null);setTokenAddress('');}} style={{flex:1,padding:'10px',background:tokenType==='erc20'?'rgba(124,99,255,0.3)':'rgba(255,255,255,0.05)',border:'1px solid',borderColor:tokenType==='erc20'?'#7C63FF':'rgba(255,255,255,0.1)',borderRadius:'10px',color:tokenType==='erc20'?'#7C63FF':'#888',fontWeight:'700',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'}}>⚡ ERC20</button>
                        <button onClick={()=>{setTokenType('spl');setTokenInfo(null);setTokenAddress('');}} style={{flex:1,padding:'10px',background:tokenType==='spl'?'rgba(153,69,255,0.3)':'rgba(255,255,255,0.05)',border:'1px solid',borderColor:tokenType==='spl'?'#9945FF':'rgba(255,255,255,0.1)',borderRadius:'10px',color:tokenType==='spl'?'#9945FF':'#888',fontWeight:'700',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'}}>◎ SPL</button>
                    </div>
                    <div style={{marginBottom:'16px'}}>
                        <label style={{display:'block',fontSize:'12px',fontWeight:'600',color:'#aaa',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>{tokenType==='spl' ? 'Mint Address' : 'Contract Address'}</label>
                        <div style={{display:'flex',gap:'8px'}}>
                            <input
                                value={tokenAddress}
                                onChange={e=>setTokenAddress(e.target.value)}
                                placeholder="0x..."
                                style={{flex:1,padding:'12px 14px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',color:'#fff',fontSize:'14px',outline:'none',fontFamily:'inherit'}}
                            />
                            <button onClick={handleLookupToken} disabled={isLoadingToken||!tokenAddress} style={{padding:'12px 16px',background:'rgba(124,99,255,0.2)',border:'1px solid rgba(124,99,255,0.3)',borderRadius:'10px',color:'#7C63FF',fontSize:'13px',fontWeight:'700',cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
                                {isLoadingToken ? '...' : 'Lookup'}
                            </button>
                        </div>
                    </div>
                    { tokenInfo && tokenInfo.needsMeta && (
                        <div style={{marginBottom:'16px'}}>
                            <div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
                                <div style={{flex:1}}>
                                    <label style={{display:'block',fontSize:'11px',color:'#aaa',marginBottom:'4px',textTransform:'uppercase'}}>Token Name</label>
                                    <input value={tokenInfo.name==='SPL Token'?'':tokenInfo.name} onChange={e=>setTokenInfo({...tokenInfo,name:e.target.value})} placeholder="e.g. RAKUN" style={{width:'100%',padding:'10px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'#fff',fontSize:'13px',outline:'none',fontFamily:'inherit',boxSizing:'border-box'}} />
                                </div>
                                <div style={{flex:1}}>
                                    <label style={{display:'block',fontSize:'11px',color:'#aaa',marginBottom:'4px',textTransform:'uppercase'}}>Symbol</label>
                                    <input value={tokenInfo.symbol.length===6&&tokenInfo.symbol===tokenInfo.address?.slice(0,6).toUpperCase()?'':tokenInfo.symbol} onChange={e=>setTokenInfo({...tokenInfo,symbol:e.target.value.toUpperCase()})} placeholder="e.g. RKN" style={{width:'100%',padding:'10px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'#fff',fontSize:'13px',outline:'none',fontFamily:'inherit',boxSizing:'border-box'}} maxLength={10} />
                                </div>
                            </div>
                        </div>
                    ) }
                    { tokenInfo && (
                        <div style={{background:'rgba(255,255,255,0.04)',borderRadius:'12px',padding:'16px',marginBottom:'16px'}}>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                                <span style={{color:'#888',fontSize:'13px'}}>Token Name</span>
                                <span style={{color:'#fff',fontSize:'13px',fontWeight:'600'}}>{tokenInfo.name}</span>
                            </div>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                                <span style={{color:'#888',fontSize:'13px'}}>Symbol</span>
                                <span style={{color:'#fff',fontSize:'13px',fontWeight:'600'}}>{tokenInfo.symbol}</span>
                            </div>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                                <span style={{color:'#888',fontSize:'13px'}}>Decimals</span>
                                <span style={{color:'#fff',fontSize:'13px',fontWeight:'600'}}>{tokenInfo.decimals}</span>
                            </div>
                            <div style={{display:'flex',justifyContent:'space-between'}}>
                                <span style={{color:'#888',fontSize:'13px'}}>Your Balance</span>
                                <span style={{color:'#00e5a0',fontSize:'13px',fontWeight:'700'}}>{parseFloat(tokenInfo.balance).toLocaleString()} {tokenInfo.symbol}</span>
                            </div>
                        </div>
                    ) }
                    { tokenInfo && (
                        <button onClick={handleAddToken} style={{display:'block',width:'100%',padding:'14px',background:'linear-gradient(135deg,#7C63FF,#FC72FF)',border:'none',borderRadius:'12px',color:'#fff',fontSize:'15px',fontWeight:'700',cursor:'pointer',fontFamily:'inherit'}}>
                            Add {tokenInfo.symbol} to Wallet
                        </button>
                    ) }
                </div>
            </div>
        ) }

        {/* MOBILE TRADE BOTTOM SHEET
            Matches the native app design — slides up from the bottom when
            user taps Swap on mobile. Shows You Pay / You Receive cards with
            a custom number keypad (no system keyboard) and token selectors.
            On desktop, Swap navigates to the full Exchange page instead. */}
        

        </div>
    );
}

export default WalletPage;